import { Link } from "react-router-dom";
import {
    TrendingUp,
    Clock,
    Hammer,
    DollarSign,
    Target,
    Briefcase,
    ArrowRight,
} from "lucide-react";
import FadeUp from "../components/FadeUp";
import ContactForm from "../components/ContactForm";
import FeaturedProject from "../components/FeaturedProject";
import { STOCK } from "../lib/stock";
import { FEATURED_PROJECT_RESIDENTIAL } from "../lib/constants";

const PACKAGES = [
    {
        title: "Pre-Listing Refresh",
        timeline: "5–10 days",
        body: "Strategic cosmetic upgrades targeted at first-impression value: paint, fixtures, hardware, lighting, deep clean.",
        items: ["Whole-home paint", "Lighting & hardware", "Curb-appeal lift", "Deep clean & stage prep"],
    },
    {
        title: "Value-Add Renovation",
        timeline: "3–6 weeks",
        body: "Comp-moving improvements where the dollars matter most: kitchens, baths, flooring, and finishes.",
        items: ["Kitchen refresh / remodel", "Bath upgrades", "Flooring throughout", "Finish-level material spec"],
    },
    {
        title: "Investor Flip Build-Out",
        timeline: "6–14 weeks",
        body: "Full-scope renovation managed against your spread, exit strategy, and timeline. Tight cost control, predictable handoffs.",
        items: ["Scope built to comps", "Permitting + inspection", "Weekly reporting", "Punch list to closing"],
    },
];

const PROCESS = [
    {
        n: "01",
        title: "Walkthrough & Strategy",
        body: "We tour the property with you, model the comps, and identify the upgrades that actually move price.",
    },
    {
        n: "02",
        title: "Scope & Cost Plan",
        body: "Line-item scope, fixed budget framework, and a clear schedule built around your listing or close date.",
    },
    {
        n: "03",
        title: "Build & Manage",
        body: "Bevel runs every trade, every delivery, every inspection. You get one point of contact.",
    },
    {
        n: "04",
        title: "List-Ready Handover",
        body: "Final QA walk, photo-ready clean, and a closing punch list, so the property hits market on schedule.",
    },
];

export default function RealEstatePartner() {
    return (
        <main data-testid="real-estate-page" className="bg-[#050505]">
            {/* HERO */}
            <section className="relative pt-40 pb-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={STOCK.realEstateHero}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-transparent to-[#050505]" />
                </div>
                <div className="container-bevel relative z-10">
                    <FadeUp>
                        <span className="overline">Real Estate Partner</span>
                    </FadeUp>
                    <FadeUp delay={1}>
                        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95] max-w-4xl">
                            Helping agents and investors{" "}
                            <span className="text-[#F5A623]">
                                increase property value.
                            </span>
                        </h1>
                    </FadeUp>
                    <FadeUp delay={2}>
                        <p className="mt-8 text-lg text-white/65 max-w-2xl leading-relaxed">
                            Bevel partners with realtors, investors, and
                            portfolio owners on pre listing and value add
                            renovations built around resale, ROI, and timeline,
                            not hourly billing.
                        </p>
                    </FadeUp>
                    <FadeUp delay={3}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact"
                                data-testid="rep-hero-cta"
                                className="btn-primary"
                            >
                                Talk to a Project Manager{" "}
                                <ArrowRight size={16} />
                            </Link>
                            <a
                                href="tel:+19183003797"
                                className="btn-secondary"
                                data-testid="rep-hero-call"
                            >
                                Call (918) 300-3797
                            </a>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* OUTCOMES STRIP */}
            <section className="bg-[#080808] hairline-top hairline-bottom">
                <div className="container-bevel grid grid-cols-1 md:grid-cols-3">
                    {[
                        {
                            icon: TrendingUp,
                            t: "Lift offers",
                            b: "Strategic finishes that move the comps and shorten days on market.",
                        },
                        {
                            icon: Clock,
                            t: "Hit the listing date",
                            b: "Timelines built around your MLS plan, not the other way around.",
                        },
                        {
                            icon: DollarSign,
                            t: "Protect the spread",
                            b: "Tight cost discipline. No mid-project surprises that erode ROI.",
                        },
                    ].map((s) => (
                        <div key={s.t} className="bg-[#080808] p-8 md:p-10">
                            <s.icon
                                size={26}
                                className="text-[#F5A623]"
                                strokeWidth={1.5}
                            />
                            <div className="font-display text-xl text-white mt-5">
                                {s.t}
                            </div>
                            <p className="text-white/60 text-sm mt-3 leading-relaxed">
                                {s.b}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED PROJECT */}
            <section
                className="section-pad"
                data-testid="rep-featured-project-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <FeaturedProject
                            testId="rep-featured-project"
                            eyebrow="Featured Project"
                            title="Pre-Listing Renovation"
                            location="Greater Tulsa"
                            description="A full pre-listing transformation managed end-to-end by Bevel. Scoped against comps, finished on schedule, and handed off list-ready. The kind of result we deliver for realtor and investor partners every cycle."
                            image={FEATURED_PROJECT_RESIDENTIAL}
                            cta={{
                                label: "Plan a Similar Project",
                                to: "/contact",
                            }}
                        />
                    </FadeUp>
                </div>
            </section>

            {/* PACKAGES */}
            <section
                className="section-pad"
                data-testid="rep-packages-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Renovation Packages</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-3xl">
                            Quick-turnaround upgrades. Real estate-grade
                            execution.
                        </h2>
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {PACKAGES.map((p, i) => (
                            <FadeUp
                                key={p.title}
                                delay={i}
                                className="bg-[#0A0A0A] p-8 md:p-10 flex flex-col"
                            >
                                <span className="overline">{p.timeline}</span>
                                <h3 className="font-display text-2xl mt-4 text-white">
                                    {p.title}
                                </h3>
                                <p className="text-white/60 mt-4 text-sm leading-relaxed">
                                    {p.body}
                                </p>
                                <ul className="mt-6 space-y-2 border-t border-white/10 pt-6">
                                    {p.items.map((it) => (
                                        <li
                                            key={it}
                                            className="flex items-center gap-3 text-sm text-white/70"
                                        >
                                            <span className="w-1.5 h-1.5 bg-[#F5A623]" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="section-pad bg-[#080808] hairline-top">
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Our Process</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
                            From walkthrough to listing-ready.
                        </h2>
                    </FadeUp>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {PROCESS.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i}
                                className="bg-[#0A0A0A] p-8 md:p-10"
                            >
                                <div className="font-display text-3xl text-[#F5A623]">
                                    {s.n}
                                </div>
                                <h3 className="font-display text-lg mt-5 text-white">
                                    {s.title}
                                </h3>
                                <p className="text-white/60 text-sm mt-3 leading-relaxed">
                                    {s.body}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA + FORM */}
            <section className="section-pad" id="contact">
                <div className="container-bevel grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <FadeUp>
                            <span className="overline">Start a Project</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                                Have a property in mind?
                            </h2>
                            <p className="text-white/60 mt-5 max-w-md">
                                Send the address and your goals: sale,
                                refinance, hold. We'll model the renovation
                                around the outcome.
                            </p>
                            <ul className="mt-8 space-y-4 text-sm text-white/70">
                                <li className="flex items-start gap-3">
                                    <Target
                                        size={16}
                                        className="text-[#F5A623] mt-1 shrink-0"
                                    />
                                    Free pre-listing walkthrough
                                </li>
                                <li className="flex items-start gap-3">
                                    <Hammer
                                        size={16}
                                        className="text-[#F5A623] mt-1 shrink-0"
                                    />
                                    Comp-driven scope of work
                                </li>
                                <li className="flex items-start gap-3">
                                    <Briefcase
                                        size={16}
                                        className="text-[#F5A623] mt-1 shrink-0"
                                    />
                                    Investor-friendly reporting
                                </li>
                            </ul>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-7">
                        <FadeUp delay={1}>
                            <ContactForm />
                        </FadeUp>
                    </div>
                </div>
            </section>
        </main>
    );
}
